
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { apiService } from '@/services/api';
import { queryKeys } from '@/lib/queryClient';

/**
 * Mutation for creating a checkout session
 * Automatically invalidates user plan cache after potential subscription changes
 */
export function useCheckoutMutation() {
  const { getToken, isSignedIn } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (priceId: string) => {
      if (!isSignedIn) {
        throw new Error('Please sign in to purchase a plan');
      }

      const token = await getToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      return apiService.createCheckoutSession(token, priceId);
    },
    onSuccess: (response) => {
      // Redirect to Stripe checkout
      window.location.href = response.url;
      
      // Invalidate user plan cache since subscription might change
      // This will refetch the plan when user returns from checkout
      queryClient.invalidateQueries({ queryKey: queryKeys.userPlan });
      
      // Also invalidate usage data since limits may change with new plan
      queryClient.invalidateQueries({ queryKey: queryKeys.usage });
    },
    onError: (error) => {
      console.error('Checkout error:', error);
    },
  });
}

/**
 * Mutation for creating a billing portal session
 * Automatically invalidates user plan cache after potential subscription changes
 */
export function useBillingPortalMutation() {
  const { getToken, isSignedIn } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!isSignedIn) {
        throw new Error('Please sign in to access billing management');
      }

      const token = await getToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      return apiService.createBillingPortalSession(token);
    },
    onSuccess: (response) => {
      window.location.href = response.url;
      
      // Get current plan to compare against
      const currentUserPlan = queryClient.getQueryData(queryKeys.userPlan) as { id: string; name: string } | null | undefined;
      const originalPlanId = currentUserPlan?.id;
      const originalPlanName = currentUserPlan?.name;
      
      // Start gentle polling with longer intervals to reduce API calls
      let pollCount = 0;
      const maxPolls = 12; // 2 minutes of polling (12 * 10 seconds)
      let foundNewPlan = false;
      
      const gentlePoll = setInterval(async () => {
        pollCount++;
        
        try {
          // Force a fresh API call, bypassing cache
          await queryClient.refetchQueries({ 
            queryKey: queryKeys.userPlan,
            type: 'active'
          });
          
          // Get the updated data from cache
          const newPlan = queryClient.getQueryData(queryKeys.userPlan) as { id: string; name: string } | null | undefined;
          
          // Check if plan has actually changed
          if (newPlan && (newPlan.id !== originalPlanId || newPlan.name !== originalPlanName)) {
            foundNewPlan = true;
            clearInterval(gentlePoll);
            
            // Force additional cache updates
            queryClient.setQueryData(queryKeys.userPlan, newPlan);
            queryClient.invalidateQueries({ queryKey: queryKeys.userPlan });
            
            // Also refresh usage data since limits may have changed with plan change
            queryClient.invalidateQueries({ queryKey: queryKeys.usage });
          }
          
          // Stop polling after max attempts
          if (pollCount >= maxPolls) {
            clearInterval(gentlePoll);
          }
          
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 10000); // Poll every 10 seconds instead of 5
      
      // Clean up polling after 3 minutes instead of 10
      setTimeout(() => {
        if (!foundNewPlan) {
          clearInterval(gentlePoll);
        }
      }, 3 * 60 * 1000);
    },
    onError: (error) => {
      console.error('Billing portal error:', error);
    },
  });
}

/**
 * Combined hook for checkout and billing portal operations
 * Provides optimized mutations with automatic cache invalidation
 */
export function useBillingOperations() {
  const checkoutMutation = useCheckoutMutation();
  const billingPortalMutation = useBillingPortalMutation();

  return {
    // Checkout operations
    createCheckout: checkoutMutation.mutate,
    isCreatingCheckout: checkoutMutation.isPending,
    checkoutError: checkoutMutation.error?.message || null,
    
    // Billing portal operations
    openBillingPortal: billingPortalMutation.mutate,
    isOpeningBillingPortal: billingPortalMutation.isPending,
    billingPortalError: billingPortalMutation.error?.message || null,
    
    // Combined states
    isProcessing: checkoutMutation.isPending || billingPortalMutation.isPending,
    error: checkoutMutation.error?.message || billingPortalMutation.error?.message || null,
    
    // Error clearing functions
    clearCheckoutError: checkoutMutation.reset,
    clearBillingPortalError: billingPortalMutation.reset,
    clearAllErrors: () => {
      checkoutMutation.reset();
      billingPortalMutation.reset();
    },
  };
}